/**
 * Shared Auth State
 *
 * Reads the JWT-based auth state managed by the host app's AuthProvider
 * (frontend/src/providers/AuthProvider.tsx) via the shared apiClient.
 * The actual provider that owns token lifecycle lives in the host app;
 * the `<AuthProvider>` export here is a passthrough kept for API
 * compatibility with existing consumers.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { apiClient } from "@shared/lib/api/client";
import { setSentryUser } from "@shared/lib/sentry";

export type AuthStatus = "loading" | "authenticated" | "unauthenticated";

export interface AuthUser {
  _id: string;
  id: string;
  email: string;
  name?: string;
  username?: string;
  avatarUrl?: string;
  emailVerificationTime?: number;
  createdAt: number;
  authProvider: "password";
  role?: string;
  isStaff?: boolean;
  isSuperuser?: boolean;
}

export interface AuthState {
  status: AuthStatus;
  user: AuthUser | null;
  isLoading: boolean;
  refreshUser: () => void;
}

export type AuthContextValue = AuthState;

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const base64 = token.split(".")[1];
    if (!base64) return null;
    const json = atob(base64.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function userFromToken(token: string | null): AuthUser | null {
  if (!token) return null;
  const payload = decodeJwtPayload(token);
  if (!payload) return null;
  const id = String(payload.user_id ?? payload.sub ?? "");
  if (!id) return null;
  const name = payload.name ? String(payload.name) : undefined;
  return {
    _id: id,
    id,
    email: String(payload.email ?? ""),
    name,
    username: name,
    avatarUrl: payload.avatar_url ? String(payload.avatar_url) : undefined,
    emailVerificationTime:
      typeof payload.email_verified_at === "number"
        ? (payload.email_verified_at as number)
        : undefined,
    createdAt:
      typeof payload.iat === "number" ? (payload.iat as number) * 1000 : Date.now(),
    authProvider: "password",
    role: payload.role ? String(payload.role) : undefined,
    isStaff: payload.is_staff === true,
    isSuperuser: payload.is_superuser === true,
  };
}

export function useAuthSnapshot(): AuthContextValue {
  const [accessToken, setAccessToken] = useState<string | null>(() =>
    apiClient.getAccessToken(),
  );
  const [bumpKey, setBumpKey] = useState(0);

  useEffect(() => {
    return apiClient.onAuthChange(({ accessToken: next }) => {
      setAccessToken(next);
    });
  }, []);

  const user = useMemo(() => userFromToken(accessToken), [accessToken, bumpKey]);

  const status: AuthStatus = accessToken ? "authenticated" : "unauthenticated";

  useEffect(() => {
    if (user) {
      setSentryUser({
        id: user._id,
        email: user.email,
        name: user.username || user.name,
      });
    } else {
      setSentryUser(null);
    }
  }, [user]);

  const refreshUser = useCallback(() => {
    setAccessToken(apiClient.getAccessToken());
    setBumpKey((n) => n + 1);
  }, []);

  return { status, user, isLoading: false, refreshUser };
}

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const value = useAuthSnapshot();
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Returns auth state. Works whether or not `<AuthProvider>` from this module
 * is mounted — falls back to a direct snapshot from `apiClient`.
 */
export function useAuthState(): AuthState {
  const ctx = useContext(AuthContext);
  const snapshot = useAuthSnapshot();
  return ctx ?? snapshot;
}

export { AuthContext };
